/**
 * WsProvider.tsx — React context provider + hook for the chatter WsClient.
 *
 * Separated from ws-client.ts (pure factory) so JSX lives in .tsx (D-WS-PROVIDER-SPLIT).
 */

import * as React from 'react';
import { createWsClient, type WsClient } from './ws-client.js';

const WsContext = React.createContext<WsClient | null>(null);
WsContext.displayName = 'WsContext';

interface WsProviderProps {
  children: React.ReactNode;
}

export function WsProvider({ children }: WsProviderProps) {
  const clientRef = React.useRef<WsClient | null>(null);

  if (!clientRef.current) {
    clientRef.current = createWsClient();
  }

  React.useEffect(() => {
    return () => {
      clientRef.current?.disconnect();
      clientRef.current = null;
    };
  }, []);

  return (
    <WsContext.Provider value={clientRef.current}>
      {children}
    </WsContext.Provider>
  );
}

export function useWsClient(): WsClient {
  const ctx = React.useContext(WsContext);
  if (!ctx) throw new Error('useWsClient called outside <WsProvider>');
  return ctx;
}
